import { Navigation, Phone, type LucideIcon } from 'lucide-react';

import { directionsUrl, telUrl } from '@/lib/navigation-links';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Card, CardBody } from './ui/card';

/**
 * A place, and the two things a rider does with a place: ring it, or navigate
 * to it.
 *
 * Used for both ends of the leg so the pharmacy and the doorway are drawn
 * identically — the rider is doing the same thing at each, and a different
 * layout for the second would be a second thing to learn at the exact moment
 * they are least able to.
 */
export function DetailBlock({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'default',
  phone,
  phoneLabel,
  directions,
  className,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string | null;
  tone?: 'default' | 'muted';
  phone?: string | null;
  phoneLabel?: string;
  directions?: { latitude?: string | null; longitude?: string | null; query?: string | null } | null;
  className?: string;
}) {
  return (
    <Card variant={tone === 'muted' ? 'sunken' : 'default'} className={className}>
      <CardBody className="grid gap-3">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'grid size-10 shrink-0 place-items-center rounded-lg',
              tone === 'muted' ? 'bg-muted text-ink-subtle' : 'bg-wash text-brand',
            )}
          >
            <Icon className="size-5" aria-hidden="true" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="font-semibold leading-snug text-ink [overflow-wrap:anywhere]">{label}</p>
            <p className="mt-0.5 text-[0.9375rem] leading-normal text-ink-muted [overflow-wrap:anywhere]">
              {value}
            </p>
            {hint ? (
              <p className="mt-1.5 text-sm leading-snug text-ink-subtle [overflow-wrap:anywhere]">
                {hint}
              </p>
            ) : null}
          </div>
        </div>

        {phone || directions ? (
          /*
           * `items-start` so one button cannot be stretched taller by the
           * other — two controls in a row that should be identical ending up
           * visibly different heights looks like a rendering bug, and ships
           * constantly.
           */
          <div className="flex flex-wrap items-start gap-2">
            {phone ? (
              <Button asChild variant="secondary" size="md">
                <a href={telUrl(phone)}>
                  <Phone aria-hidden="true" />
                  {phoneLabel ?? 'Call'}
                </a>
              </Button>
            ) : null}

            {directions ? (
              <Button asChild variant="secondary" size="md">
                {/*
                 * A new tab, not this one. Navigating away from a delivery
                 * mid-leg would cost the rider the screen they are working
                 * from, and on iOS the maps handoff does not always come back.
                 */}
                <a href={directionsUrl(directions)} target="_blank" rel="noopener noreferrer">
                  <Navigation aria-hidden="true" />
                  Directions
                </a>
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
